/**
 * Fuel/energy economy math — ported to match Android `FuelEconomy.kt` so the web
 * shows the SAME numbers as the phones (verified via golden fixtures).
 *
 * Canonical inputs are metric: distance in km, volume in litres, energy in kWh.
 * Display conversion (mi / gal / MPG / Wh-mi) happens here, never in storage.
 *
 * ⚠ PARITY NOTE (do not "fix" unilaterally): Android's per-segment
 * `consumptionValue` for IMPERIAL fuel returns miles-per-litre while labelling it
 * "MPG", whereas the whole-history `efficiency()` uses the proper km/L→MPG factor
 * (2.35214583). These two disagree by ~3.785×. We replicate Android's behaviour
 * exactly for parity; reconciling it must be coordinated across all three clients
 * (see WEBAPP_TODO testing/golden-fixtures).
 */
import type { FuelLog } from "../model.ts";

const KM_PER_MI = 0.621371;
const L_PER_GAL = 3.785411784;
const KMPL_TO_MPG = 2.35214583;

export interface Economy {
  metric: boolean;
  distanceLabel(): string;
  volumeLabel(): string;
  efficiencyLabel(): string;
  energyEfficiencyLabel(): string;
  economyLabel(isEV: boolean): string;
  betterIsLower(isEV: boolean): boolean;
  convertDistance(km: number): number;
  convertVolume(litres: number): number;
  /** Inverse of convertDistance: display distance (km or mi) → canonical km. */
  toKm(displayDistance: number): number;
  /** Inverse of convertVolume: display volume (L or gal) → canonical litres. */
  toLitres(displayVolume: number): number;
  /** Per-segment economy in the active unit, or null. Mirrors Android exactly. */
  consumptionValue(distanceKm: number, qty: number, isEV: boolean): number | null;
  /**
   * Whole-history average economy over full-fill segments, formatted, or "—".
   * Fuel: L/100km or MPG (proper). Electric: kWh/100km or Wh/mi.
   */
  efficiency(logs: FuelLog[]): string;
  /**
   * Real-world consumption in DISPLAY volume per 100 display-distance
   * (L/100km, gal/100mi, kWh/100km) — always per-100, never MPG. Full-to-full
   * method incl. partial fills. Null until ≥2 usable segments.
   */
  measuredConsumptionPer100(logs: FuelLog[], electric: boolean): number | null;
}

export function makeEconomy(metric: boolean): Economy {
  const convertDistance = (km: number) => (metric ? km : km * KM_PER_MI);
  const convertVolume = (l: number) => (metric ? l : l / L_PER_GAL);

  const fmt1 = (v: number, unit: string) => `${v.toFixed(1)} ${unit}`;

  const efficiencyLabel = () => (metric ? "L/100km" : "MPG");
  const energyEfficiencyLabel = () => (metric ? "kWh/100km" : "Wh/mi");

  function consumptionValue(distanceKm: number, qty: number, isEV: boolean): number | null {
    if (distanceKm <= 0 || qty <= 0) return null;
    if (isEV) {
      return metric ? (qty / distanceKm) * 100 : (qty * 1000) / (distanceKm * KM_PER_MI);
    }
    return metric ? (qty / distanceKm) * 100 : (distanceKm * KM_PER_MI) / qty;
  }

  const isEVSet = (logs: FuelLog[]) => logs.some((l) => l.kwhAdded != null);

  function efficiencyICE(logs: FuelLog[]): string {
    const full = logs.filter((l) => l.isFullFill).sort((a, b) => a.odometer - b.odometer);
    if (full.length < 2) return "—";
    let dist = 0, vol = 0;
    for (let i = 1; i < full.length; i++) {
      const d = full[i]!.odometer - full[i - 1]!.odometer;
      if (d > 0) { dist += d; vol += full[i]!.volumeAdded; }
    }
    if (dist <= 0) return "—";
    return metric ? fmt1((vol / dist) * 100, "L/100km") : fmt1((dist / vol) * KMPL_TO_MPG, "MPG");
  }

  function efficiencyEV(logs: FuelLog[]): string {
    const s = logs.filter((l) => (l.kwhAdded ?? 0) > 0).sort((a, b) => a.odometer - b.odometer);
    if (s.length < 2) return "—";
    let dist = 0, kwh = 0;
    for (let i = 1; i < s.length; i++) {
      const d = s[i]!.odometer - s[i - 1]!.odometer;
      if (d > 0) { dist += d; kwh += s[i]!.kwhAdded ?? 0; }
    }
    if (dist <= 0) return "—";
    return metric ? fmt1((kwh / dist) * 100, "kWh/100km") : fmt1((kwh * 1000) / (dist * KM_PER_MI), "Wh/mi");
  }

  function measuredConsumptionPer100(logs: FuelLog[], electric: boolean): number | null {
    const relevant = logs
      .filter((l) => (electric ? (l.kwhAdded ?? 0) > 0 : l.volumeAdded > 0))
      .sort((a, b) => a.odometer - b.odometer);
    if (relevant.length < 2) return null;
    const qtyOf = (l: FuelLog) => (electric ? (l.kwhAdded ?? 0) : l.volumeAdded);

    let totalDistanceKm = 0;
    let totalQtyNative = 0;
    const anchors = relevant.filter((l) => l.isFullFill);
    if (anchors.length >= 2) {
      for (let i = 1; i < anchors.length; i++) {
        const d = anchors[i]!.odometer - anchors[i - 1]!.odometer;
        if (d <= 0) continue;
        const lo = anchors[i - 1]!.odometer, hi = anchors[i]!.odometer;
        const qty = relevant
          .filter((l) => l.odometer > lo && l.odometer <= hi)
          .reduce((s, l) => s + qtyOf(l), 0);
        totalDistanceKm += d;
        totalQtyNative += qty;
      }
    } else {
      for (let i = 1; i < relevant.length; i++) {
        const d = relevant[i]!.odometer - relevant[i - 1]!.odometer;
        if (d <= 0) continue;
        totalDistanceKm += d;
        totalQtyNative += qtyOf(relevant[i]!);
      }
    }
    if (totalDistanceKm <= 0 || totalQtyNative <= 0) return null;
    const displayDistance = convertDistance(totalDistanceKm);
    const displayQty = electric ? totalQtyNative : convertVolume(totalQtyNative);
    if (displayDistance <= 0) return null;
    return (displayQty / displayDistance) * 100;
  }

  return {
    metric,
    distanceLabel: () => (metric ? "km" : "mi"),
    volumeLabel: () => (metric ? "L" : "gal"),
    efficiencyLabel,
    energyEfficiencyLabel,
    economyLabel: (isEV) => (isEV ? energyEfficiencyLabel() : efficiencyLabel()),
    betterIsLower: (isEV) => metric || isEV,
    convertDistance,
    convertVolume,
    toKm: (d) => (metric ? d : d / KM_PER_MI),
    toLitres: (v) => (metric ? v : v * L_PER_GAL),
    consumptionValue,
    efficiency: (logs) => (isEVSet(logs) ? efficiencyEV(logs) : efficiencyICE(logs)),
    measuredConsumptionPer100,
  };
}
